import React, { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Dimensions,
  AppState,
  AppStateStatus,
} from 'react-native';

import TabBar from '../../components/TabBar';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../theme/themeTokens';
import { v2GetMyBalances, v2GetRates, v2GetRatesHistory, v2GetTokens, v2GetCard, v2GetCardWallet, v2GetMyPushNotifications, v2GetAppSettings } from '../../services/v2/api';
import type { V2Balance, V2RateHistoryPoint, V2RateInfo, V2Token } from '../../types/v2';
import { getTokenIconSource , getLocalTokenIcon} from '../../tools/getCurrencyIcon';
import AccountCard from '../../components/UIComponents/AccountCard';
import AccountActionRow from '../../components/AccountActionRow';
import HideableAccountRow from '../../components/UIComponents/HideableAccountRow';
import TokenCard from '../../components/UIComponents/TokenCard';
import { useHiddenAccounts, keyForCurrent, keyForDeposit, keyForCard, keyForStock, keyForInvestment } from '../../contexts/HiddenAccountsContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import DashboardHeader from '../../components/DashboardHeader';
import { notificationService } from '../../services/notifications';
import { formatDepositMeta } from '../../utils/depositMeta';
import { isFiatToken, isXStockToken } from '../../utils/marketChart';
import { isStockAccountBalance, xStockTicker } from '../../utils/xstocks';
import { byUsdtValue, byUsdtValueUsdtFirst } from '../../utils/balanceOrder';
import {
  CARD_ICON_COLUMN, CHART_WIDTH, COLUMN_GAP, NAME_MIN_WIDTH, PRICE_WIDTH,
} from '../../components/UIComponents/tokenRowLayout';
import { useXStocks } from '../../hooks/useXStocks';
import StockIcon from '../../components/UIComponents/StockIcon';
import type { V2XStock } from '../../types/v2';

const BALANCE_POLL_MS = 10_000;

interface DashboardScreenProps {
  onNavigate?: (screen: string) => void;
  onBalancePress?: (balance: V2Balance) => void;
  onCardPress?: (balance: V2Balance) => void;
  onInvestmentPress?: (balance: V2Balance) => void;
  /** Open Deposit screen — picks a default balance in the parent. */
  onInflow?: () => void;
  /** Open Withdraw screen — picks a default balance in the parent. */
  onWithdraw?: () => void;
  /** Open DepositCard (top-up) screen — picks a default card in the parent. */
  onTopUp?: () => void;
  /** Dashboard Exchange button → Operations on the Exchange tab. */
  onExchange?: () => void;
  onTokenPress?: (token: V2Token) => void;
  /** A stock tile on the Dashboard — opens the same screen Popular tokens does. */
  onStockPress?: (stock: V2XStock) => void;
}

// Parse amount string to number, fallback 0
const parseAmount = (amount: string): number => {
  const num = parseFloat(amount);
  return isNaN(num) ? 0 : num;
};

/**
 * Merge balances that share the same token_symbol into a single synthetic entry
 * whose amount and value_usd are the sum of all merged entries.
 * All other fields (icon, name, etc.) are taken from the first balance in the group.
 */
const groupBalancesBySymbol = (bals: V2Balance[]): V2Balance[] => {
  const groups = new Map<string, V2Balance[]>();
  for (const b of bals) {
    const existing = groups.get(b.token_symbol);
    if (existing) {
      existing.push(b);
    } else {
      groups.set(b.token_symbol, [b]);
    }
  }
  return Array.from(groups.values()).map(group => {
    if (group.length === 1) { return group[0]; }
    const totalAmount = group.reduce((sum, b) => sum + parseAmount(b.amount), 0);
    const totalValueUsd = group.reduce((sum, b) => sum + parseAmount(b.value_usd ?? '0'), 0);
    // available_amount + locked_amount must also be summed across the
    // group — otherwise the dashboard card would pick group[0]'s
    // single-network values and display only one network's funds. For
    // a user with USDT on both Ethereum and Tron that meant the
    // "Current accounts" tile showed 100k or 120k instead of 220k.
    const totalAvailable = group.reduce(
      (sum, b) => sum + parseAmount(b.available_amount ?? b.amount),
      0,
    );
    const totalLocked = group.reduce(
      (sum, b) => sum + parseAmount(b.locked_amount ?? '0'),
      0,
    );
    return {
      ...group[0],
      amount: String(totalAmount),
      value_usd: String(totalValueUsd),
      available_amount: String(totalAvailable),
      locked_amount: String(totalLocked),
    };
  });
};

interface PopularToken {
  token: V2Token;
  symbol: string;
  iconUrl?: string | null;
  changePercent: number;
  value: number;
  history: V2RateHistoryPoint[];
}

const DashboardV2Screen: React.FC<DashboardScreenProps> = ({ onNavigate, onBalancePress, onCardPress, onInvestmentPress, onInflow, onWithdraw, onTopUp, onExchange, onTokenPress, onStockPress }) => {
  const { isVisibleOnDashboard, getOverride, setOverride } = useHiddenAccounts();
  // The stock catalogue: whether the section exists at all, and the 24h
  // move each tile shows. A client may still hold shares after the feature
  // is switched off — the section goes with the feature, not with the
  // holdings.
  const xStocks = useXStocks();
  const snackbar = useSnackbar();
  const { firebaseUser, handleSessionExpired, user } = useAuth();
  const userLogin = user?.login || '-----';

  const [balances, setBalances] = useState<V2Balance[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalUsd, setTotalUsd] = useState<string>('0');
  // What the client owns, including what a pending request holds. Falls back
  // to totalUsd against a backend that predates the field.
  const [totalOwnedUsd, setTotalOwnedUsd] = useState<string | null>(null);

  // Card balance amounts fetched from Payoma via /api/v2/user/cards/:id
  // Key = wallet_address (card ID), Value = amount in major units (e.g. 27.85 EUR)
  const [cardAmounts, setCardAmounts] = useState<Record<string, number>>({});

  // Last-4 digits of each card's PAN, extracted from the same Payoma response
  // used for balance. Shown as "**** 3476" subtitle on the card tile when the
  // admin-controlled `show_card_last4` app-setting is true.
  // Key = wallet_address (card ID), Value = the 4 trailing digits as a string.
  const [cardLast4, setCardLast4] = useState<Record<string, string>>({});

  // Per-card wallet name shown as the card-tile title (e.g. "Personal" /
  // "Travel Card") so the user can distinguish multiple cards of the same
  // currency. Pulled from /api/v2/user/cards/:id/wallet — same source the
  // CardScreen and DepositCardScreen use.
  // Key = wallet_address (card ID), Value = the wallet name string.
  const [cardWalletNames, setCardWalletNames] = useState<Record<string, string>>({});

  // App-wide UX flag fetched from /api/v2/app/settings on mount. Defaults to
  // false so a slow/failed settings fetch never accidentally shows last4 —
  // surfacing it must be an explicit admin opt-in.
  const [showCardLast4, setShowCardLast4] = useState(false);

  const [popularTokens, setPopularTokens] = useState<PopularToken[]>([]);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [ratesError, setRatesError] = useState<string | null>(null);
  // Symbols of popular tokens fetched from the tokens list (is_popular_token === true)
  const [popularSymbols, setPopularSymbols] = useState<string[]>([]);
  // Map symbol -> icon_url from the tokens API (used to display S3 icons)
  const popularIconMapRef = React.useRef<Record<string, string | null>>({});
  const popularTokenMapRef = React.useRef<Record<string, V2Token>>({});

  // Whether user has unread push notifications (for bell icon)
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [showEqvTooltip, setShowEqvTooltip] = useState(false);

  /** Build the list of popular token symbols from the tokens API and cache it. */
  const fetchPopularSymbols = useCallback(async (): Promise<string[]> => {
    try {
      const res = await v2GetTokens();
      const seen = new Set<string>();
      const symbols: string[] = [];
      const iconMap: Record<string, string | null> = {};
      const tokenMap: Record<string, V2Token> = {};
      for (const t of res.tokens) {
        const symbol = t.symbol.toUpperCase();
        // Fiat is a rate, not a market — see isFiatToken.
        if (t.is_popular_token && !isXStockToken(t) && !isFiatToken(t) && !seen.has(symbol)) {
          seen.add(symbol);
          symbols.push(symbol);
          iconMap[symbol] = t.icon_url ?? null;
          tokenMap[symbol] = t;
        }
      }
      popularIconMapRef.current = iconMap;
      popularTokenMapRef.current = tokenMap;
      setPopularSymbols(symbols);
      return symbols;
    } catch {
      // Fallback to whatever we had before, or empty list
      return popularSymbols;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRates = useCallback(async () => {
    try {
      setRatesLoading(true);
      setRatesError(null);
      const symbols = await fetchPopularSymbols();
      if (symbols.length === 0) {
        setPopularTokens([]);
        return;
      }
      const [latestResult, historyResult] = await Promise.allSettled([
        v2GetRates(symbols),
        v2GetRatesHistory(symbols, '7d'),
      ]);
      if (latestResult.status === 'rejected') {
        throw latestResult.reason;
      }
      const response = latestResult.value;
      const history = historyResult.status === 'fulfilled' ? historyResult.value.series : {};
      const tokens: PopularToken[] = symbols
        .map(symbol => {
          const info: V2RateInfo | undefined = response.rates[symbol];
          return {
            token: popularTokenMapRef.current[symbol],
            symbol,
            iconUrl: popularIconMapRef.current[symbol] ?? null,
            changePercent: parseFloat(info?.change_24h ?? '0') || 0,
            value: parseFloat(info?.price_usd ?? '0') || 0,
            history: history[symbol] ?? [],
          };
        })
		.filter(token => Boolean(token.token))
        .sort((a, b) => b.value - a.value)
        .slice(0, 4);
      setPopularTokens(tokens);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch rates';
      setRatesError(msg);
    } finally {
      setRatesLoading(false);
    }
  }, [fetchPopularSymbols]);

  /** Fetch card balance amounts (and last-4 PAN digits) from Payoma for all
   *  card-type balances in one sweep. The same v2GetCard response carries
   *  both — extracting last4 here avoids a second round-trip to Payoma's
   *  sensitive /cards/:id/number endpoint just to render a UI hint. */
  const fetchCardAmounts = useCallback(async (allBalances: V2Balance[]) => {
    const cards = allBalances.filter(
      b => (b.account_type_code === 'card' || b.account_type_code === 'card_balance') && b.wallet_address,
    );
    if (cards.length === 0) { return; }

    const amounts: Record<string, number> = {};
    const last4: Record<string, string> = {};
    const walletNames: Record<string, string> = {};
    await Promise.all(
      cards.map(async (b) => {
        const cardId = b.wallet_address!;
        // Fire both Payoma fetches in parallel — card-detail carries
        // amount/last4, wallet endpoint carries the user-set name. They
        // don't depend on each other so we don't serialize them.
        const [cardRes, walletRes] = await Promise.allSettled([
          v2GetCard(cardId),
          v2GetCardWallet(cardId),
        ]);

        if (cardRes.status === 'fulfilled') {
          try {
            const data: any = cardRes.value?.data ?? {};
            const walletBalance = data?.wallet?.balance;
            const precision: number = data?.wallet?.currency?.precision ?? 2;
            if (walletBalance && walletBalance.amount != null) {
              amounts[cardId] = walletBalance.amount / Math.pow(10, precision);
            }
            // Last 4 PAN digits — Payoma's GET /card/:id returns this under
            // `number` (verified empirically against test stand). The other
            // names are kept as fallbacks in case Payoma rotates the schema
            // or older backends still expose `masked_pan`/`pan`/`card_number`.
            const panCandidate =
              data?.number ??
              data?.masked_pan ??
              data?.pan ??
              data?.card_number ??
              data?.external_id ??
              '';
            const digits = String(panCandidate).replace(/\D/g, '');
            if (digits.length >= 4) {
              last4[cardId] = digits.slice(-4);
            }
          } catch {
            // Silently ignore — will fallback to balance.amount, no last4
          }
        }

        if (walletRes.status === 'fulfilled') {
          const name = walletRes.value?.data?.name;
          if (typeof name === 'string' && name.length > 0) {
            walletNames[cardId] = name;
          }
        }
      }),
    );
    setCardAmounts(prev => ({ ...prev, ...amounts }));
    setCardLast4(prev => ({ ...prev, ...last4 }));
    setCardWalletNames(prev => ({ ...prev, ...walletNames }));
  }, []);

  /** Fetch OneApp UX flags from app_settings. Defaults to all-off on failure
   *  so a slow network never accidentally surfaces an opt-in feature. */
  const fetchAppSettings = useCallback(async () => {
    try {
      const res = await v2GetAppSettings();
      const raw = res?.settings?.show_card_last4;
      setShowCardLast4(raw === true || raw === 'true');
    } catch {
      // silent — keep current (or default-false) state
    }
  }, []);

  /** Check if there are any unviewed push notifications */
  const fetchUnreadStatus = useCallback(async () => {
    try {
      const res = await v2GetMyPushNotifications();
      const unread = (res.notifications || []).some(n => !n.viewed_at);
      setHasUnreadNotifications(unread);
    } catch {
      // silent — non-critical
    }
  }, []);

  const fetchBalances = useCallback(async () => {
    if (!firebaseUser) { return; }
    try {
      setLoading(true);
      setError(null);
      const balancesRes = await v2GetMyBalances();

      const fetchedBalances = balancesRes.balances || [];
      setBalances(fetchedBalances);
      setTotalUsd(balancesRes.total_usd ?? '0');
      setTotalOwnedUsd(balancesRes.total_owned_usd ?? null);

      // Fetch card amounts from Payoma in the background (non-blocking)
      fetchCardAmounts(fetchedBalances);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load balances';
      if (
        errorMessage.includes('Session expired') ||
        errorMessage.includes('Not authenticated')
      ) {
        handleSessionExpired(errorMessage);
        return;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, handleSessionExpired, fetchCardAmounts]);

  const onRefresh = useCallback(async () => {
    if (!firebaseUser) { return; }
    setRefreshing(true);
    setError(null);
    setRatesError(null);

    await Promise.all([
      // ── Balances — собственный catch, не даёт блокировать обновление токенов
      v2GetMyBalances()
        .then(res => {
          const fetchedBalances = res.balances || [];
          setBalances(fetchedBalances);
          setTotalUsd(res.total_usd ?? '0');
          setTotalOwnedUsd(res.total_owned_usd ?? null);
          // Refresh card amounts from Payoma in the background
          fetchCardAmounts(fetchedBalances);
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Failed to fetch balances';
          if (
            msg.includes('Session expired') ||
            msg.includes('Not authenticated')
          ) {
            handleSessionExpired(msg);
          } else {
            setError(msg);
          }
        }),

      // ── Rates and 7-day sparklines
      fetchRates(),

      // ── Unread notifications status
      fetchUnreadStatus(),

      // ── OneApp UX flags (show_card_last4 etc.) — re-fetched here so an
      //    admin toggle reflects on the next pull-to-refresh, without
      //    requiring a re-login or full app restart.
      fetchAppSettings(),
    ]).finally(() => {
      setRefreshing(false);
    });
  }, [firebaseUser, handleSessionExpired, fetchRates, fetchCardAmounts, fetchUnreadStatus, fetchAppSettings]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!firebaseUser) {
      handleSessionExpired();
    }
  }, [firebaseUser, handleSessionExpired]);

  useEffect(() => {
    fetchBalances();
    fetchRates();
    fetchUnreadStatus();
  }, [fetchBalances, fetchRates, fetchUnreadStatus]);

  // Load OneApp UX flags on mount AND on pull-to-refresh (see onRefresh).
  // Defaults to all-off so a slow/failed fetch never surfaces an opt-in
  // feature; admin must explicitly enable the flag.
  useEffect(() => {
    fetchAppSettings();
  }, [fetchAppSettings]);

  // Push о Payoma-транзакции в foreground → принудительно обновить Total balance.
  // Backend к моменту получения push'а уже подтянул свежий баланс с Payoma в БД,
  // так что повторный fetchBalances() сразу покажет новую сумму. Снимаем
  // обработчик на unmount, чтобы не дёргать setState уже размонтированного экрана.
  useEffect(() => {
    notificationService.setOnBalanceRefreshHandler(() => {
      fetchBalances();
    });
    return () => {
      notificationService.setOnBalanceRefreshHandler(null);
    };
  }, [fetchBalances]);

  // Resume from background → refetch balances. Покрывает edge case когда
  // юзер свернул app до confirm'а транзакции на admin'e: foreground push
  // мы пропустили (OS показала только баннер, app'у сообщение не пришло),
  // и при возврате state баланса устаревший. AppState 'active' event
  // фитрится по previous state !== 'active' чтобы не дёргать fetch на
  // каждом micro-transition (напр. iOS notification tray).
  useEffect(() => {
    let prev = AppState.currentState;
    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (prev !== 'active' && next === 'active') {
        fetchBalances();
      }
      prev = next;
    });
    return () => {
      sub.remove();
    };
  }, [fetchBalances]);

  // The web gets no balance push, so a change made on the server after an
  // action (a closed deposit: held → moved to the current account → closed)
  // only showed after leaving the screen and coming back. Poll while the
  // screen is open and the tab is visible; quietly — no loading state. Card
  // amounts (two Payoma calls per card) keep their mount/pull-to-refresh schedule.
  useEffect(() => {
    if (Platform.OS !== 'web' || !firebaseUser) { return; }
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') { return; }
      v2GetMyBalances()
        .then(res => {
          setBalances(res.balances || []);
          setTotalUsd(res.total_usd ?? '0');
          setTotalOwnedUsd(res.total_owned_usd ?? null);
        })
        .catch(() => { /* the next tick or a manual refresh tries again */ });
    }, BALANCE_POLL_MS);
    return () => clearInterval(timer);
  }, [firebaseUser]);

  if (!firebaseUser) {
    return null;
  }

  // Derived data from v2 API — group by account_type_code returned from
  // server. Every block reads biggest-first in USDT; see balanceOrder.
  const currentAccountBalances = balances
    .filter(b => (!b.account_type_code || b.account_type_code === 'current') && b.provider_name === 'virtual');

  // После rename миграции 00098 codes совпадают с UI labels:
  //   account_type='deposit'    = "Deposits" block (classic срочный)
  //   account_type='investment' = "Investments" block (T-Fund-like, бывш. t_fund)
  //
  // These two were ordered by `updated_at` while every other block went by
  // value, so a client's deposits read 63 877, 455 958, 135 940.
  const investmentAccountBalances = balances
    .filter(b => b.account_type_code === 'deposit')
    .sort(byUsdtValue);

  const tFundAccountBalances = balances
    .filter(b => b.account_type_code === 'investment')
    .sort(byUsdtValue);

  const cardBalances = balances
    .filter(b => b.account_type_code === 'card' || b.account_type_code === 'card_balance')
    .sort(byUsdtValue);

  // Tokenized stocks, largest holding first — the same order All assets
  // uses for them.
  const xStockBalances = balances
    .filter(isStockAccountBalance)
    .sort(byUsdtValue);

  // Group current account balances by token_symbol so that e.g. USDT on
  // Ethereum and USDT on Tron are shown as a single card with the combined
  // amount — then order the groups. Ordering the rows first put a client's
  // 100 + 100 USDT below a single BTC worth 150, because the sort never
  // saw the 200 the tile shows.
  const groupedCurrentAccountBalances = groupBalancesBySymbol(currentAccountBalances)
    .sort(byUsdtValueUsdtFirst);

  // Two figures from the server: what the client owns goes in the headline,
  // what they can move goes in the line under it. They differ only while a
  // request holds something — a withdrawal in flight, a card top-up, a
  // deposit being closed — and the second line appears only then.
  const availableUSDT = parseAmount(totalUsd);
  const totalBalanceUSDT = totalOwnedUsd !== null ? parseAmount(totalOwnedUsd) : availableUSDT;
  const hasLockedFunds = totalBalanceUSDT - availableUSDT >= 0.01;
  // Same shape as the headline: NBSP thousands, comma decimal, two places.
  const availableFormatted = availableUSDT
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d)\.)/g, '\u00A0')
    .replace('.', ',');

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <Image
        source={require('../../assets/Background.png')}
        style={styles.backgroundCircles}
        resizeMode="cover"
        pointerEvents="none"
      />

      <DashboardHeader
        onNavigate={onNavigate}
        hasUnreadNotifications={hasUnreadNotifications}
        variant="dashboard"
      />

      {/* Внешний скролл: всё кроме заголовка и TabBar */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        directionalLockEnabled
        scrollEnabled={scrollEnabled}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={COLORS.white}
            colors={[COLORS.white]}
            progressBackgroundColor={COLORS.primaryNavy}
          />
        }
      >
        {/* Тёмная секция: баланс + активы */}
        <View style={styles.topSection}>
        <View style={styles.balanceContainer}>
          {/* Строка 1: "Total balance, USDT" + знак вопроса */}
          <TouchableOpacity
            style={styles.balanceTitleRow}
            activeOpacity={0.7}
            onPress={() => {
              setShowEqvTooltip(v => !v);
            }}
          >
            <Text style={styles.balanceTitleText}>Total balance, USDT</Text>
          </TouchableOpacity>
          {showEqvTooltip && (
            <Text style={styles.eqvTooltipText}>eqv. USDT · 30 min</Text>
          )}

          {/* Строка 2: значение баланса */}
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              onNavigate?.('history');
            }}
          >
            {loading ? (
              <Text style={styles.balanceValue}>...</Text>
            ) : (() => {
              const rawFormatted = totalBalanceUSDT.toFixed(2);
              const [rawInt, rawDec] = rawFormatted.split('.');
              const intPart = rawInt.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
              return (
                <View style={styles.balanceValueRow}>
                  <Text style={styles.balanceValue}>{intPart}</Text>
                  <Text style={styles.balanceValueDecimal}>,{rawDec}</Text>
                </View>
              );
            })()}
          </TouchableOpacity>

          {/* Only while something is held. For most clients the two figures
              are the same number and this line never appears. */}
          {!loading && hasLockedFunds && (
            <Text style={styles.availableText}>
              {`Available ${availableFormatted} USDT`}
            </Text>
          )}
        </View>

        {/* Inflow / Withdraw / Top-up / Exchange row — handlers resolved in
            App.tsx against the first matching balance (no per-button picker
            yet). */}
        <AccountActionRow
          onInflow={onInflow}
          onWithdraw={onWithdraw}
          onTopUp={onTopUp}
          onExchange={onExchange}
        />

          <View style={styles.assetsWrapper}>

            {/* Current accounts */}
            {loading ? (
              <ActivityIndicator
                size="small"
                color={COLORS.white}
                style={styles.loadingIndicator}
              />
            ) : error ? (
              <TouchableOpacity onPress={fetchBalances} style={styles.retryRow}>
                <Text style={styles.retryText}>⚠ {error}  Retry</Text>
              </TouchableOpacity>
            ) : (
              <>
                {/* Visibility rule on Dashboard:
                    - Default: show iff amount > 0.
                    - 'hide' override: never show (regardless of amount).
                    - 'show' override: always show (even if amount = 0).
                    Same data is rendered on All Assets, where the user can
                    restore or re-add anything. */}
                {(() => {
                  const visibleCurrent = groupedCurrentAccountBalances.filter((b) => {
                    const k = keyForCurrent(b.token_symbol);
                    // Default visibility: USDT always pinned (it's the
                    // user's primary asset), other tokens only if non-zero.
                    // Explicit overrides ('hide' / 'show') still win.
                    const defaultVisible =
                      b.token_symbol === 'USDT' || parseAmount(b.amount) > 0;
                    return isVisibleOnDashboard(k, defaultVisible);
                  });
                  if (visibleCurrent.length === 0) { return null; }
                  return (
                    <>
                      <Text style={styles.accountLabel}>Current accounts</Text>
                      {visibleCurrent.map(balance => (
                        <HideableAccountRow
                          key={balance.token_symbol}
                          isHidden={false}
                          swipeIcon="eyeclose"
                          onSwipeStart={() => setScrollEnabled(false)}
                          onSwipeEnd={() => setScrollEnabled(true)}
                          onToggle={() => {
                            const k = keyForCurrent(balance.token_symbol);
                            const prev = getOverride(k) ?? null;
                            setOverride(k, 'hide');
                            snackbar.show({
                              message: 'Removed from Dashboard',
                              actionLabel: 'Undo',
                              onAction: () => setOverride(k, prev),
                            });
                          }}
                        >
                          <AccountCard
                            style={styles.accountCardWrapper}
                            data={{
                              icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                              iconFallback: getLocalTokenIcon(balance.token_symbol),
                              name: `${balance.token_name}, ${balance.token_symbol}`,
                              // The whole balance, as every other tile shows,
                              // with the held part called out underneath. It
                              // used to show only the available part, which
                              // made a client with a withdrawal in flight
                              // read their own current account as smaller
                              // than it is, with nothing saying why.
                              valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                              value: parseAmount(balance.amount),
                              locked: parseAmount(balance.locked_amount ?? '0'),
                              changePercent: parseFloat(balance.change ?? '0'),
                              decimals: balance.token_decimals,
                            }}
                            showChangePercent={false}
                            showValueUSDT={balance.token_symbol !== 'USDT'}
                            onPress={() => onBalancePress?.(balance)}
                            spacious
                          />
                        </HideableAccountRow>
                      ))}
                    </>
                  );
                })()}
                {/* A card is a thing the client holds whether or not there
                    is money on it, so unlike a balance it shows by default
                    and only an explicit swipe puts it away. */}
                {(() => {
                  const visibleCards = cardBalances.filter((b) =>
                    isVisibleOnDashboard(keyForCard(b.account_id ?? b.id), true),
                  );
                  if (visibleCards.length === 0) { return null; }
                  return (
                  <>
                    <Text style={[styles.accountLabel, styles.investmentAccountLabel]}>
                      Cards
                    </Text>
                    {visibleCards.map(balance => {
                      const cardAmount = balance.wallet_address ? cardAmounts[balance.wallet_address] : undefined;
                      const last4 = balance.wallet_address ? cardLast4[balance.wallet_address] : undefined;
                      const walletName = balance.wallet_address ? cardWalletNames[balance.wallet_address] : undefined;
                      const subtitle = showCardLast4 && last4 ? `**** ${last4}` : undefined;
                      return (
                        <HideableAccountRow
                          key={balance.id}
                          isHidden={false}
                          swipeIcon="eyeclose"
                          onSwipeStart={() => setScrollEnabled(false)}
                          onSwipeEnd={() => setScrollEnabled(true)}
                          onToggle={() => {
                            const k = keyForCard(balance.account_id ?? balance.id);
                            const prev = getOverride(k) ?? null;
                            setOverride(k, 'hide');
                            snackbar.show({
                              message: 'Removed from Dashboard',
                              actionLabel: 'Undo',
                              onAction: () => setOverride(k, prev),
                            });
                          }}
                        >
                          <AccountCard
                            style={styles.accountCardWrapper}
                            data={{
                              icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                              iconFallback: getLocalTokenIcon(balance.token_symbol),
                              // Wallet name distinguishes cards of the same
                              // currency ("Personal" / "Travel"); fall back to
                              // currency label until the per-card fetch lands.
                              name: walletName ?? `${balance.token_name}, ${balance.token_symbol}`,
                              valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                              value: cardAmount ?? parseAmount(balance.amount),
                              locked: parseAmount(balance.locked_amount ?? '0'),
                              changePercent: parseFloat(balance.change ?? '0'),
                              decimals: balance.token_decimals,
                            }}
                            subtitle={subtitle}
                            showValueUSDT={false}
                            showChangePercent={false}
                            onPress={() => onCardPress?.(balance)}
                            spacious
                          />
                        </HideableAccountRow>
                      );
                    })}
                  </>
                  );
                })()}
                {/* Tokenized stocks. The section belongs to the feature, not
                    to the holdings: with xStocks switched off it is gone
                    even from a client who still holds shares. An empty
                    position stays off by default, as an empty balance does. */}
                {(() => {
                  if (!xStocks.enabled) { return null; }
                  const visibleStocks = xStockBalances.filter((b) =>
                    isVisibleOnDashboard(keyForStock(b.account_id ?? b.id), parseAmount(b.amount) > 0),
                  );
                  if (visibleStocks.length === 0) { return null; }
                  return (
                  <>
                    <Text style={[styles.accountLabel, styles.investmentAccountLabel]}>
                      xStocks
                    </Text>
                    {visibleStocks.map(balance => {
                      const stock = xStocks.stocks.find(x => x.symbol === balance.token_symbol);
                      return (
                        <HideableAccountRow
                          key={balance.id}
                          isHidden={false}
                          swipeIcon="eyeclose"
                          onSwipeStart={() => setScrollEnabled(false)}
                          onSwipeEnd={() => setScrollEnabled(true)}
                          onToggle={() => {
                            const k = keyForStock(balance.account_id ?? balance.id);
                            const prev = getOverride(k) ?? null;
                            setOverride(k, 'hide');
                            snackbar.show({
                              message: 'Removed from Dashboard',
                              actionLabel: 'Undo',
                              onAction: () => setOverride(k, prev),
                            });
                          }}
                        >
                          <AccountCard
                            style={styles.accountCardWrapper}
                            data={{
                              iconNode: (
                                <StockIcon
                                  symbol={balance.token_symbol}
                                  iconUrl={stock?.icon_url ?? balance.icon_url}
                                  size={32}
                                />
                              ),
                              name: xStockTicker(balance.token_symbol),
                              valueUSDT: parseAmount(balance.value_usd ?? '0'),
                              value: parseAmount(balance.amount),
                              locked: parseAmount(balance.locked_amount ?? '0'),
                              // The day's move comes from the catalogue; a
                              // balance carries no price of its own.
                              changePercent: parseFloat(stock?.change_24h ?? '0') || 0,
                              decimals: balance.token_decimals,
                            }}
                            percentSuffix="24h"
                            onPress={stock ? () => onStockPress?.(stock) : undefined}
                            spacious
                          />
                        </HideableAccountRow>
                      );
                    })}
                  </>
                  );
                })()}
                {/* Deposits follow the same rule as current accounts: an
                    empty one stays off the Dashboard until the user adds it
                    back from All assets. Keyed per account — several
                    deposits can share a currency. */}
                {(() => {
                  const visibleDeposits = investmentAccountBalances.filter((b) =>
                    isVisibleOnDashboard(keyForDeposit(b.account_id ?? b.id), parseAmount(b.amount) > 0),
                  );
                  if (visibleDeposits.length === 0) { return null; }
                  return (
                  <>
                    <Text style={[styles.accountLabel, styles.investmentAccountLabel]}>
                      Deposit Accounts
                    </Text>
                    {visibleDeposits.map(balance => {
                      // Unified deposit caption — "USDT · 3 months · 3%".
                      // APY lives inside the name line via
                      // formatDepositMeta, so the right-side percent is
                      // suppressed to avoid showing the rate twice.
                      const apyFraction = balance.apy ? parseFloat(balance.apy) : null;
                      return (
                        <HideableAccountRow
                          key={balance.id}
                          isHidden={false}
                          swipeIcon="eyeclose"
                          onSwipeStart={() => setScrollEnabled(false)}
                          onSwipeEnd={() => setScrollEnabled(true)}
                          onToggle={() => {
                            const k = keyForDeposit(balance.account_id ?? balance.id);
                            const prev = getOverride(k) ?? null;
                            setOverride(k, 'hide');
                            snackbar.show({
                              message: 'Removed from Dashboard',
                              actionLabel: 'Undo',
                              onAction: () => setOverride(k, prev),
                            });
                          }}
                        >
                          <AccountCard
                            style={styles.accountCardWrapper}
                            data={{
                              icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                              iconFallback: getLocalTokenIcon(balance.token_symbol),
                              name: formatDepositMeta(balance.token_symbol, balance.term, apyFraction),
                              valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                              value: parseAmount(balance.amount),
                              locked: parseAmount(balance.locked_amount ?? '0'),
                              changePercent: 0,
                              decimals: balance.token_decimals,
                            }}
                            showValueUSDT={balance.token_symbol !== 'USDT'}
                            showChangePercent={false}
                            onPress={() => onInvestmentPress?.(balance)}
                            spacious
                          />
                        </HideableAccountRow>
                      );
                    })}
                  </>
                  );
                })()}
                {/* Like a card: the account exists whether or not it holds
                    anything, so it shows until swiped away. */}
                {(() => {
                  const visibleTFund = tFundAccountBalances.filter((b) =>
                    isVisibleOnDashboard(keyForInvestment(b.account_id ?? b.id), true),
                  );
                  if (visibleTFund.length === 0) { return null; }
                  return (
                  <>
                    {/* T-FUND, as the Products screen calls it. */}
                    <Text style={[styles.accountLabel, styles.investmentAccountLabel]}>
                      T-FUND
                    </Text>
                    {visibleTFund.map(balance => {
                      // Investment account-type tile. Tap открывает
                      // DepositAccountScreen — тот же handler что и для
                      // классического deposit'а (см. handleOpenInvestment
                      // Deposit в App.tsx), screen умеет рендерить оба
                      // account-type'а через IsDepositLikeAccountTypeCode.
                      // Name приоритезирует account.name (custom-ввод
                      // админа при создании Investment account'а), fallback
                      // на formatDepositMeta для legacy без name.
                      const apyFraction = balance.apy ? parseFloat(balance.apy) : null;
                      return (
                        <HideableAccountRow
                          key={balance.id}
                          isHidden={false}
                          swipeIcon="eyeclose"
                          onSwipeStart={() => setScrollEnabled(false)}
                          onSwipeEnd={() => setScrollEnabled(true)}
                          onToggle={() => {
                            const k = keyForInvestment(balance.account_id ?? balance.id);
                            const prev = getOverride(k) ?? null;
                            setOverride(k, 'hide');
                            snackbar.show({
                              message: 'Removed from Dashboard',
                              actionLabel: 'Undo',
                              onAction: () => setOverride(k, prev),
                            });
                          }}
                        >
                          <AccountCard
                            style={styles.accountCardWrapper}
                            data={{
                              icon: getTokenIconSource(balance.token_symbol, balance.icon_url),
                              iconFallback: getLocalTokenIcon(balance.token_symbol),
                              name: balance.account_name || formatDepositMeta(balance.token_symbol, balance.term, apyFraction),
                              valueUSDT: parseAmount(balance.value_usd ?? balance.amount),
                              value: parseAmount(balance.amount),
                              locked: parseAmount(balance.locked_amount ?? '0'),
                              changePercent: 0,
                              decimals: balance.token_decimals,
                            }}
                            showValueUSDT={balance.token_symbol !== 'USDT'}
                            showChangePercent={false}
                            onPress={() => onInvestmentPress?.(balance)}
                            spacious
                          />
                        </HideableAccountRow>
                      );
                    })}
                  </>
                  );
                })()}
                {currentAccountBalances.length === 0 &&
                  investmentAccountBalances.length === 0 &&
                  tFundAccountBalances.length === 0 &&
                  xStockBalances.length === 0 &&
                  cardBalances.length === 0 && (
                    <Text style={styles.emptyText}>No accounts yet</Text>
                  )}
              </>
            )}
          </View>
        </View>

        {/* All assets button */}
        <TouchableOpacity
          style={styles.allAssetsBtn}
          activeOpacity={0.8}
          onPress={() => {
            onNavigate?.('allAssets');
          }}
        >
          <Text style={styles.allAssetsBtnText}>All assets</Text>
        </TouchableOpacity>

        {/* Светлая секция: Popular tokens — без собственного скролла */}
        <View style={styles.lightSection}>
          {/* Popular tokens header */}
          <View style={styles.popularHeader}>
            <Text style={styles.lightSectionTitle}>Popular tokens</Text>
            <TouchableOpacity
              style={styles.viewAllRow}
              activeOpacity={0.7}
              onPress={() => {
                onNavigate?.('popularTokens');
              }}
            >
              <Text style={styles.viewAllText}>View all</Text>
              <Image
                source={require('../../assets/arrow_left_20.png')}
                style={styles.lightsectionArrow}
              />
            </TouchableOpacity>
          </View>

          {/* Mirrors TokenCard's row by hand — same widths, same gap, so the
              labels stand over the columns they name. The trend column keeps
              its width and loses its icon: a chart glyph over a row of charts
              said nothing the charts did not. */}
          <View style={styles.marketColumnHeader}>
            <View style={styles.marketIconColumn} />
            <Text style={[styles.marketColumnText, styles.marketCoinColumn]}>Coin</Text>
            <View style={styles.marketGap} />
            <Text style={[styles.marketColumnText, styles.marketPriceColumn]}>Price, USDT</Text>
            <View style={styles.marketGap} />
            <View style={styles.marketTrendColumn} />
          </View>

          {/* Popular token rows with 7-day sparklines */}
          <View style={styles.tokensGrid}>
            {ratesLoading ? (
              <ActivityIndicator
                size="small"
                color={COLORS.primaryNavy}
                style={styles.loadingIndicator}
              />
            ) : ratesError ? (
              <TouchableOpacity onPress={fetchRates} style={styles.retryRow}>
                <Text style={[styles.retryText, { color: COLORS.primaryNavy }]}>
                  ⚠ {ratesError}  Retry
                </Text>
              </TouchableOpacity>
            ) : (
              popularTokens.map((token, index) => (
                <TokenCard
                  key={token.symbol}
                  style={index === 0 ? styles.tokenCard : [styles.tokenCard, styles.tokenCardSpacing]}
                  // The rows sit straight on the light section — no white card.
                  backgroundColor="transparent"
                  data={{
                    icon: getTokenIconSource(token.symbol, token.iconUrl),
                    iconFallback: getLocalTokenIcon(token.symbol),
                    symbol: token.symbol,
                    changePercent: token.changePercent,
                    value: token.value,
                    // How many decimals a price carries comes from the token
                    // catalogue, not from the screen.
                    valueDecimals: token.token?.decimals,
                    valueCurrency: 'USDT',
                    history: token.history,
                  }}
                  onPress={() => onTokenPress?.(token.token)}
                />
              ))
            )}
          </View>

          {/* The design carries both: "View all" in the section header and
              this one under the list. The header link is easy to miss at
              the end of a scroll, which is where a client who has just
              read the four rows actually is. The web keeps only "View all". */}
          {Platform.OS !== 'web' && (
            <TouchableOpacity
              style={styles.seeAllButton}
              activeOpacity={0.8}
              onPress={() => onNavigate?.('popularTokens')}
            >
              <Text style={styles.seeAllText}>See all</Text>
            </TouchableOpacity>
          )}

          <View style={styles.bottomSpacer} />
        </View>
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate || (() => {})} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  backgroundCircles: {
    position: 'absolute',
    width: Dimensions.get('window').width + 14,
    height: 320,
    top: -15,
    left: -7,
  },

  headerTitleRow: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleCol: {
    alignItems: 'center',
  },
  headerTitleInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  headerLogin: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.white,
    textAlign: 'center',
  },

  // ── Top dark section (inside scroll) ─────────────────────
  topSection: {
    backgroundColor: 'transparent',
    paddingBottom: 0,
    overflow: 'visible',
    position: 'relative',
  },
  balanceContainer: {
    alignItems: 'center',
    marginTop: -14,
    position: 'relative',
    height: 124,
    paddingHorizontal: 36,
    overflow: 'visible',
    justifyContent: 'center',
  },
  balanceTitleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  balanceTitleText: {
    color: COLORS.white,
    fontSize: 16,
    fontWeight: '400',
    opacity: 0.75,
    marginRight: 4,
  },

  balanceLabelIcon: {
    width: 10,
    height: 10,
    marginLeft: 0,
    marginTop: -2,
  },
  balanceValue: {
    color: COLORS.white,
    fontFamily: Platform.select({ ios: 'HelveticaNeue-Medium', android: 'sans-serif-medium' }),
    fontSize: 32,
    fontWeight: '500',
    lineHeight: 48,
    letterSpacing: 0.5,
  },
  balanceValueDecimal: {
    color: COLORS.blueSteel,
    fontSize: 18,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  balanceValueRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
  },

  // ── Outer scroll (flex container) ──────────────────────
  scrollView: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: 'transparent',
  },

  // ── Light section: Popular tokens (no inner scroll) ──────
  lightSection: {
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    paddingHorizontal: 16,
    paddingTop: 20,
    flexGrow: 1,
  },

  // ── Section headers ───────────────────────────────────
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: '500',
    color: COLORS.white,
    lineHeight: 32,
  },
  sectionArrow: {
    fontSize: 30,
    fontWeight: '200',
    color: COLORS.white,
    marginTop: -4,
  },

  lightSectionTitle: {
    fontSize: 18,
    fontWeight: '400',
    color: COLORS.primaryNavy,
    lineHeight: 32,
  },
  lightsectionArrow: {
    width: 18,
    height: 18,
    marginLeft: 4,
    transform: [{ rotate: '180deg' }],
  },
  addButton: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: COLORS.brandOrange,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    color: COLORS.white,
    fontSize: 26,
    fontWeight: '300',
    lineHeight: 28,
  },

  // ── Assets wrapper ──────────────────────────────────────────────────────────────────────
  assetsWrapper: {
    paddingHorizontal: 20,
    marginTop: 0,
    paddingBottom: 0,
  },
  loadingIndicator: {
    marginVertical: 12,
  },

  // ── Current account row ───────────────────────────────────────────────────────────────────
  accountLabel: {
    fontSize: 18,
    fontWeight: '400',
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  investmentAccountLabel: {
    marginTop: 12,
  },
  accountCardWrapper: {
    marginBottom: 12,
  },
  retryRow: {
    paddingVertical: 8,
  },
  retryText: {
    fontSize: 15,
    color: COLORS.brandOrange,
  },
  emptyText: {
    fontSize: 16,
    color: COLORS.blueSteel,
    paddingVertical: 8,
  },

  // ── Popular tokens ────────────────────────────────────
  popularHeader: {
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tokensGrid: {
    width: '100%',
  },
  tokenCard: {
    width: '100%',
  },
  viewAllRow: { flexDirection: 'row', alignItems: 'center' },
  viewAllText: { color: COLORS.primaryNavy, fontSize: 12 },
  marketColumnHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 2, marginBottom: 12 },
  marketColumnText: { color: COLORS.blueSteel, fontFamily: 'Inter', fontSize: 14, fontWeight: '500' },
  // The row's own geometry (TokenCard, tokenRowLayout), from the icon's
  // width on: the coin column takes the slack down to its floor, then a
  // gap, the price right-aligned, another gap, and the chart's width —
  // empty, because a label over a row of charts said nothing the charts
  // did not. Every part shrinks here exactly as it does in the row, which
  // is what keeps the labels over their columns on a narrow phone.
  // Empty, and the width of the card's icon — the column the names start
  // behind. Leave it out and the header divides a different remainder than
  // the row does once the name hits its floor.
  marketIconColumn: { width: CARD_ICON_COLUMN },
  marketCoinColumn: { flex: 1, minWidth: NAME_MIN_WIDTH },
  marketGap: { width: COLUMN_GAP, flexShrink: 1 },
  marketPriceColumn: { width: PRICE_WIDTH, textAlign: 'right' },
  marketTrendColumn: { width: CHART_WIDTH, flexShrink: 1 },
  // 12 between rows, as the design spaces them.
  tokenCardSpacing: { marginTop: 12 },
  seeAllButton: {
    height: 40,
    borderRadius: 8,
    backgroundColor: COLORS.brandOrange,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 24,
    marginBottom: 20,
  },
  seeAllText: { color: COLORS.white, fontFamily: 'Inter', fontSize: 16, fontWeight: '500' },

  bottomSpacer: {
    height: 110,
  },

  eqvTooltip: {
    position: 'absolute',
    bottom: '100%',
    right: -95,
    marginBottom: 4,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },

  eqvTooltipText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontFamily: 'Inter',
  },
  // Inter, like the "Total balance" line above it. 'Urbanist-Medium' is not
  // bundled anywhere: the phone fell back to the system font, the browser to
  // its default serif.
  availableText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 14,
    fontFamily: 'Inter',
    marginTop: 4,
  },
  allAssetsBtn: {
    height: 40,
    backgroundColor: '#C94021',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 20,
    marginTop: 8,
    marginBottom: 16,
  },
  allAssetsBtnText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
  },
});

export default DashboardV2Screen;

